// src/pages/Pickup/Pickup.js
import React, { useState, useEffect, useRef } from "react";
import { QrReader } from "react-qr-reader";
import { Icon, Modal, Input, Spin, message } from "antd";
import { bindActionCreators } from "redux";
import { tokenAction } from "../../redux-stuffs/actions/token_action";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { saveCashRequestDto } from "../../redux-stuffs/actions/profile_action";
import {
  merchantAddressState,
  merchantNameState,
  nameState,
  phoneState,
  amountState,
  accessTokenState,
} from "../../atoms";
import withRecoil from "../../withRecoil";
import api from "../../api/api";
import queryString from "query-string";
import Header from "../../components/Header";
import Barcode from "./Barcode";
import HelpHeader from "../HelpHeader";
import { colors } from "../Colors";
import { withTranslation } from "react-i18next";
import WalmartPickup from "./WalmartPickup";

const Pickup = (props) => {
  const {
    t,
    token,
    cashReqDto,
    history,
    recoilStates,
    location,
  } = props;
  const isDemoEmbed = Boolean(location?.state?.fromDemo);

  const [isApiLoading, setIsApiLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [pin, setPin] = useState("");
  const [isPinModalOpen, setIsPinModalOpen] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isMerchantOpen, setIsMerchantOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [completeReq] = useState(false); // keeping for parity, though unused
  const [isQROpen, setIsQROpen] = useState(false);
  const [showText, setShowText] = useState(true);
  const [showNFC, setShowNFC] = useState(false);
  const [barcodeData, setBarcodeData] = useState(null);
  const [barcodeError, setBarcodeError] = useState("");
  const [createdAt, setCreatedAt] = useState(null);
  const [activeTab, setActiveTab] = useState("Phone");

  const pollingIntervalRef = useRef(null);

  const merchantName = recoilStates.merchantNameState.state;
  const isWalmart = merchantName?.toLowerCase().includes("walmart");
  const { fee } = location.state || {};

  // ---------- helpers / handlers ----------

  const startPolling = () => {
    if (!pollingIntervalRef.current) {
      pollingIntervalRef.current = setInterval(requestStatus, 3000);
    }
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const handleScan = (data, error) => {
    if (data) {
      setResult(data.text);
      setIsScanning(false);
      openPinModal();
    }
    if (error) {
      console.info(error);
    }
  };

  const handleBarcodeData = () => {
    const { zipcode, uuid } = location.state || {};

    setBarcodeError("");
    setBarcodeData(null);

    let phoneNumber = recoilStates.phoneState.state;
    if (phoneNumber && phoneNumber.substring(0, 2).includes("+1")) {
      phoneNumber = phoneNumber.slice(2);
    }

    const body = {
      access_token: recoilStates.accessTokenState.state,
      cash_request_id: cashReqDto.cash_request_id,
      customerId: uuid,
      requestId: cashReqDto.cash_request_id,
      firstName: (recoilStates.nameState.state || "").split(" ")[0],
      lastName: (recoilStates.nameState.state || "")
        .split(" ")
        .slice(1)
        .join(" "),
      phoneNumber,
      amount: (recoilStates.amountState.state || 0) * 100,
      zipcode,
    };

    fetch(`${api.extra}/api/greendot_barcode_creation`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: queryString.stringify(body),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Barcode creation failed");
        return res.json();
      })
      .then((data) => {
        setBarcodeData(data.barcodeId);
        setBarcodeError("");
      })
      .catch(() => {
        setBarcodeError(t("pickup:barcode_failed"));
        setBarcodeData(null);
        closeMerchantSide();
        Modal.confirm({
          title: t("pickup:failed"),
          content: t("pickup:barcode_failed"),
          onOk: callCancelApi,
          okText: t("pickup:back_to_dashboard"),
          okButtonProps: {
            className: "action-button btn",
          },
          maskClosable: false,
          okCancel: false,
        });
      });
  };

  const sendPayload = async (pinValue, resultValue) => {
    const apiUrl = `${api.main}/ccm/verify_request/qr`;

    try {
      const parsed = JSON.parse(resultValue);
      const qr_uuid = parsed.qr_uuid;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          access_token: token,
          qr_uuid,
          pin: pinValue,
        }),
      });

      const responseData = await response.json();
      return responseData;
    } catch (e) {
      throw new Error("Invalid QR payload");
    }
  };

  const handlePin = async () => {
    setErrorMessage("");
    try {
      const responseData = await sendPayload(pin, result);
      if (responseData.status === 104) {
        setIsSuccess(true);
        setErrorMessage("");
        setIsPinModalOpen(false);
        openMerchantSide();
      } else {
        setErrorMessage(t("pickup:incorrect_pin"));
        setPin("");
      }
    } catch (err) {
      console.error("Error with pin: ", err);
      setErrorMessage(t("pickup:please_try_again"));
    }
  };

  const openPinModal = () => {
    setIsPinModalOpen(true);
    startPolling();
  };

  const openMerchantSide = () => {
    setIsMerchantOpen(true);
  };

  const closePinModal = () => {
    setIsPinModalOpen(false);
    setPin("");

    if (isMerchantOpen) {
      setResult(null);
      setIsScanning(false);
    } else {
      // keep result, start scanning again
      setIsScanning(true);
    }
  };

  const closeMerchantSide = () => {
    setIsMerchantOpen(false);
  };

  const handlePinChange = (e) => {
    setPin(e.target.value);
  };

  const requestStatus = async () => {
    const apiUrl = `${api.main}/cash_request_status`;
    const cashRequest = cashReqDto.cash_request_id;

    fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        access_token: token,
        cash_request_id: cashRequest,
      }),
    })
      .then((response) => response.json())
      .then((responseData) => {
        if (!createdAt) {
          setCreatedAt(responseData.data && responseData.data.created_at);
        }

        if (responseData.status === "403") {
          callCancelApi();
          stopPolling();
          closeMerchantSide();
          Modal.confirm({
            title: t("pickup:failed_withdrawal"),
            content: responseData.message,
            onOk: () => history.push("/dashboard/amount-available"),
          });
        } else if (responseData.data && responseData.data.status === "Complete") {
          fetch(`${api.main}/ccm/complete_request`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              access_token: token,
              request_id: cashRequest,
              status: "Complete",
            }),
          });
          history.push({
            pathname: "/dashboard/complete",
          });
          stopPolling();
          closeMerchantSide();
        }
      })
      .catch((err) => {
        console.error("Error", err && err.message ? err.message : err);
        message.error(t("pickup:please_try_again"));
      });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handlePin();
    }
  };

  const goBackToPhoneVerification = () => {
    setIsMerchantOpen(false);
    setActiveTab("Phone");
    setShowText(true);
    setIsQROpen(false);
  };

  const callCancelApi = () => {
    setIsApiLoading(true);
    const body = {
      cash_request_id: cashReqDto.cash_request_id,
      action: 0,
      access_token: recoilStates.accessTokenState.state,
    };

    fetch(`${api.main}/request_action_by_customer`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
      },
      body: queryString.stringify(body),
    })
      .then(() => {
        setIsApiLoading(false);
        history.push({
          pathname: "/dashboard",
        });
      })
      .catch(() => {
        setIsApiLoading(false);
      });
  };

  const cancelCashReq = () => {
    Modal.confirm({
      className: "spare-confirm",
      title: t("pickup:are_you_sure"),
      content: (
        t("pickup:cancel_irreversible")
      ),
      onOk: callCancelApi,
      okText: t("pickup:cancel_request"),
      cancelText: t("pickup:go_back"),
      okButtonProps: { className: "cancel-button-compact btn" },
      cancelButtonProps: { className: "action-button-secondary-compact btn" },
      styles: {
        content: {
          borderRadius: 20,
          overflow: "hidden",
          backgroundClip: "padding-box",
        },
      },
    });
  };

  const handleTabClick = (tabName, action) => {
    setActiveTab(tabName);
    action();

    if (tabName === "Phone") {
      startPolling();
    } else {
      stopPolling();
    }
  };

  const handleScanQRCode = async () => {
    if (isQROpen) {
      // CLOSE scanner
      setIsScanning(false);
      setIsQROpen(false);
      setShowText(true);
      setShowNFC(false);
      return;
    }

    // OPEN scanner
    try {
      await navigator.mediaDevices.getUserMedia({ video: true });
      setIsScanning(true);
      setIsQROpen(true);
      setShowText(false);
      setShowNFC(false);
    } catch (error) {
      console.error("Camera access denied:", error);
      setIsScanning(false);
      setIsQROpen(false);
    }
  };

  const handleShowText = () => {
    setIsScanning(false);
    setIsQROpen(false);
    setShowText(true);
    setShowNFC(false);
  };

  const handleNFC = () => {
    setIsScanning(false);
    setIsQROpen(false);
    setShowText(false);
    setShowNFC(true);
  };

  const renderPinModal = () => (
    <Modal
      visible={isPinModalOpen}
      onOk={handlePin}
      onCancel={closePinModal}
      closable={false}
      width={window.innerWidth < 576 || isDemoEmbed ? "90%" : "fit-content"}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        maxWidth: isDemoEmbed ? "360px" : undefined,
      }}
      bodyStyle={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
        textAlign: "center",
      }}
      footer={[
        <button
          key="cancel"
          type="button"
          className="cancel-button-compact btn"
          onClick={closePinModal}
          style={{ width: "80px", height: "30px" }}
        >
          {t("pickup:cancel")}
        </button>,
        <button
          key="ok"
          type="button"
          className="action-button-compact btn"
          onClick={handlePin}
          style={{ width: "80px", height: "30px" }}
        >
          {t("pickup:confirm")}
        </button>,
      ]}
    >
      <div>
        <p>
          <b>{t("pickup:enter_pin")}</b>
        </p>
        <Input
          type="password"
          value={pin}
          onChange={handlePinChange}
          onKeyDown={handleKeyPress}
          style={{ textAlign: "center", width: "180px" }}
        />
        {errorMessage && (
          <p style={{ color: "red", margin: "8px 0 0" }}>{errorMessage}</p>
        )}
      </div>
    </Modal>
  );

  const renderMerchantModal = () => (
    <Modal
      title={t("pickup:waiting_for_merchant")}
      visible={isMerchantOpen}
      closable={false}
      width={window.innerWidth < 576 ? "100%" : 520}
      {...(isDemoEmbed ? {
        width: "90%",
        style: { maxWidth: "360px", margin: "0 auto" },
        bodyStyle: { maxHeight: "60vh", overflowY: "auto" },
      } : {})}
      onCancel={cancelCashReq}
      footer={[
        <div
          style={{ display: "flex", justifyContent: "space-between", width: "100%" }}
          key="footer"
        >
          <button
            key="submit"
            onClick={goBackToPhoneVerification}
            style={{
              border: "1px solid gray",
              borderRadius: "50px",
              fontSize: "15px",
              padding: "10px 10px",
              color: "gray",
              backgroundColor: "white",
            }}
          >
            {t("pickup:go_back")}
          </button>
          <button
            key="submit"
            type="primary"
            onClick={cancelCashReq}
            style={{
              border: "1px solid red",
              borderRadius: "50px",
              fontSize: "15px",
              padding: "10px 10px",
              color: "red",
              backgroundColor: "white",
            }}
          >
            {t("pickup:cancel_request")}
          </button>
        </div>,
      ]}
    >
      {isSuccess && (
        <div>
          <p>{t("pickup:wait_for_merchant")}</p>
        </div>
      )}
    </Modal>
  );

  useEffect(() => {
    const init = async () => {
      await requestStatus();
      startPolling();
      const merchant = recoilStates.merchantNameState.state;
      const walmart = merchant?.toLowerCase().includes("walmart");
      if (walmart) {
        handleBarcodeData();
      }
    };
    init();

    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- shared right-side info panel ----------
  const InfoPanel = ({ showPin }) => (
    <div className="flex-item">
      <div className="info-container">
        <div className="info-header">
          <h4>{t("pickup:pickup_amount")}</h4>
          <h4>${recoilStates.amountState.state}</h4>
        </div>
        <div className="sub-header">
          <p style={{ color: colors.spareBlue }}>{t("pickup:fee")}</p>
          <p style={{ color: colors.spareBlue }}>${fee}</p>
        </div>
      </div>

      <div className="location-info" style={{ border: "1px solid #ddd" }}>
        <div>
          <p>{t("pickup:merchant_for_pickup")}</p>
        </div>
        <div>
          {isWalmart ? (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignContent: "center",
                justifyContent: "center",
              }}
            >
              <img
                src="/images/Walmart-Logo.png"
                alt="Walmart"
                style={{ maxHeight: "50px" }}
              />
              <p>®</p>
            </div>
          ) : (
            <h4 style={{ fontWeight: "bold" }}>{merchantName}</h4>
          )}
        </div>
        <div>
          <p>{recoilStates.merchantAddressState.state}</p>
        </div>
      </div>

      {showPin && (
        <div
          className="location-info"
          style={{ border: "1px solid #ddd", marginTop: "5px" }}
        >
          <div>
            <h4 style={{ fontWeight: "bold" }}>
              {t("pickup:pin_label")} {cashReqDto.pin}
            </h4>
          </div>
        </div>
      )}
    </div>
  );

  // ---------- NON-WALMART RENDER ----------
  const renderNonWalmartView = () => (
    <div className="withdrawl-page">
      <Header />
      <div className="container withdrawl">
        <div className="spare-dvs align-items-start">
          <div className="w-100 float-left">
            <div className="withdrawl-information">
              <div
                style={{
                  position: "relative",
                  marginBottom: "20px",
                  textAlign: "center",
                  width: "100%",
                }}
              >
                <h2>{t("pickup:title")}</h2>
                <HelpHeader />
              </div>
              <div
                className="withdrawl-qr"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <h4
                  dangerouslySetInnerHTML={{
                    __html: t("pickup:instructions"),
                  }}
                />
                <div className="withdrawl-actions">
                  <button
                    onClick={() => handleTabClick("QR", handleScanQRCode)}
                    className="action-button btn"
                    style={{ borderRadius: "100px" }}
                  >
                    {t("pickup:qr_button")}
                  </button>
                  <button
                    className="cancel-button btn"
                    onClick={cancelCashReq}
                  >
                    {t("pickup:cancel_pickup")}
                  </button>
                </div>
              </div>
            </div>

            <Spin
              spinning={isApiLoading && isQROpen}
              indicator={
                <Icon type="loading" style={{ fontSize: 50, color: "#94220d" }} />
              }
            >
              <div className="pickup-container">
                {isQROpen && (
                  <div className="flex-item qr">
                    <div className="flex-item qr">
                      <div className="content-box">
                        <div className="QR-btn">
                          {isScanning ? (
                            <QrReader
                              onResult={handleScan}
                              showViewFinder
                              constraints={{
                                aspectRatio: 1,
                                facingMode: { ideal: "environment" },
                              }}
                              className="verify-btn"
                            />
                          ) : (
                            <h4 style={{ margin: 10 }}>
                              {t("pickup:camera_unavailable")}
                            </h4>
                          )}
                        </div>
                        {renderPinModal()}
                        {renderMerchantModal()}
                      </div>

                      {showNFC && (
                        <div className="content-box">
                          <h4 style={{ margin: 10 }}>
                            {t("pickup:nfc_prompt")}
                          </h4>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <InfoPanel showPin />
              </div>
            </Spin>
          </div>
        </div>
      </div>
    </div>
  );

  // ---------- final render ----------

  return isWalmart ? <WalmartPickup
    recoilStates={recoilStates}
    fee={fee}
    cancelCashReq={cancelCashReq}
    isApiLoading={isApiLoading}
    barcodeData={barcodeData}
    barcodeError={barcodeError}
    t={t}
  /> : renderNonWalmartView();
};

// redux wiring

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      tokenAction,
      saveCashRequestDto,
    },
    dispatch
  );

const mapStateToProps = (state) => {
  return {
    token: state.token,
    profile: state.profile,
    cashReqDto: state.cashReqDto,
  };
};

export default withTranslation("pickup")(
  connect(mapStateToProps, mapDispatchToProps)(
    withRouter(
      withRecoil(Pickup, [
        accessTokenState,
        merchantAddressState,
        merchantNameState,
        nameState,
        phoneState,
        amountState,
      ])
    )
  )
);
